import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface YesNoSwitchProps {
  checked: boolean;
  /** Flips the value; rejects to leave the switch where it was. */
  onToggle: () => Promise<void>;
  disabled?: boolean;
  'aria-label': string;
  'data-testid'?: string;
}

/**
 * A yes/no fact the desk flips on a row — paid, confirmed — drawn as a switch
 * that is red or green rather than the theme's on/off, because a desk reading
 * down a column of forty wants to see the "no"s from across the room.
 *
 * The word beside it says which it is, for anyone the colour alone does not
 * tell. The switch goes quiet while the save is in flight and comes back to
 * whatever the row now says, so a refused save shows as "still no" rather than
 * a tick that was never real.
 */
export default function YesNoSwitch({
  checked,
  onToggle,
  disabled,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: YesNoSwitchProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onToggle();
    } catch {
      // The page has already said what went wrong; the switch just stays put.
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="inline-flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled || busy}
        onCheckedChange={handle}
        aria-label={ariaLabel}
        data-testid={testId}
        className={cn(
          'data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-red-500',
          'focus-visible:ring-green-600 data-[state=unchecked]:focus-visible:ring-red-500'
        )}
      />
      <span
        className={cn(
          'w-6 text-xs font-medium',
          checked ? 'text-green-700 dark:text-green-500' : 'text-red-600 dark:text-red-400'
        )}
      >
        {checked ? t('gymAdmin.registrations.yes') : t('gymAdmin.registrations.no')}
      </span>
    </label>
  );
}
